import React from 'react';
import styled, { css } from 'styled-components';
import Icon from '../Icon/Icon';
import { focusRing, primitiveColors, device } from '@/styles/template';

type ExitButtonProps = {
    onClick?: () => void;
    areaSize?: number;
    iconSize?: number;
    /** Вариант для страницы тестирования — иконка в цвете overlay */
    testing?: boolean;
    /** Компактный крестик 3rem/2rem на ОБОИХ брейкпоинтах (мобайл тоже). */
    xSmall?: boolean;
    className?: string;
    /** Доступное имя кнопки для screen reader. */
    'aria-label'?: string;
    /** Ref на корневую `<button>` — нужен модалке для автофокуса при открытии. */
    ref?: React.Ref<HTMLButtonElement>;
};

const MOBILE_AREA = 7;
const MOBILE_ICON = 2.5;
const XSMALL_AREA = 3;
const XSMALL_ICON = 2;

/**
 * Семантически — `<button>`, а не `<div>`: только так браузер сам
 * подхватывает фокус через `Tab`, активацию через `Enter`/`Space` и
 * правильно объявляет элемент screen reader-у. Глобальный сброс
 * `outline: 0` в `src/styles/global.ts` компенсируем единым
 * `focusRing` из template — кольцо появляется только при клавиатурной
 * навигации (`:focus-visible`).
 */
const Area = styled.button<{ $areaSize: number; $xSmall?: boolean }>`
    /* mobile first — жёстко фиксированный размер (xSmall — компактный) */
    width: ${p => (p.$xSmall ? XSMALL_AREA : MOBILE_AREA)}rem;
    height: ${p => (p.$xSmall ? XSMALL_AREA : MOBILE_AREA)}rem;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    background: transparent;
    color: inherit;
    padding: 0;
    border: 0;

    ${focusRing}

    /* десктоп — кастомный размер из пропа (xSmall остаётся компактным) */
    @media ${device.tablet} {
        width: ${p => (p.$xSmall ? XSMALL_AREA : p.$areaSize)}rem;
        height: ${p => (p.$xSmall ? XSMALL_AREA : p.$areaSize)}rem;
    }
`;

const CloseIcon = styled(Icon)<{ $iconSize: number; $testing?: boolean; $xSmall?: boolean }>`
    /* mobile first — жёстко фиксированный размер иконки (xSmall — компактный) */
    && {
        width: ${p => (p.$xSmall ? XSMALL_ICON : MOBILE_ICON)}rem;
        height: ${p => (p.$xSmall ? XSMALL_ICON : MOBILE_ICON)}rem;
    }

    /* десктоп — кастомный размер из пропа (xSmall остаётся компактным) */
    @media ${device.tablet} {
        && {
            width: ${p => (p.$xSmall ? XSMALL_ICON : p.$iconSize)}rem;
            height: ${p => (p.$xSmall ? XSMALL_ICON : p.$iconSize)}rem;
        }
    }

    ${({ $testing, theme }) => $testing && css`
        color: ${theme.semantic?.surface?.overlay ?? primitiveColors.alpha.black25};
    `}
`;

const ExitButton: React.FC<ExitButtonProps> = ({
    onClick,
    areaSize = MOBILE_AREA,
    iconSize = MOBILE_ICON,
    testing = false,
    xSmall = false,
    className,
    'aria-label': ariaLabel,
    ref,
}) => (
    <Area
        ref={ref}
        type="button"
        $areaSize={areaSize}
        $xSmall={xSmall}
        onClick={onClick}
        className={className}
        aria-label={ariaLabel}
    >
        <CloseIcon
            name="Close"
            $iconSize={iconSize}
            color={testing ? undefined : 'default'}
            $testing={testing}
            $xSmall={xSmall}
        />
    </Area>
);

export default ExitButton;
